import './reels.css';
import type {Store} from '../lib/store.ts';
import type {Reel} from '../models/reel.ts';
import type {CardComponent} from '../pages/reader/lib/cards.ts';
import {muteIcon, unmuteIcon} from './icons/reel-icons.ts';

export type VideoPlayerOptions = {
	muted: boolean;
};

export class ReelView implements CardComponent {
	reel: Reel;
	el: HTMLElement;

	#isListening = false;
	store: Store;
	#eventListeners: [HTMLElement, string, EventListener][] = [];
	#visibilityObserver: IntersectionObserver | null = null;
	#isActive = false;
	#hasStarted = false;

	constructor(reel: Reel, store: Store) {
		this.reel = reel;
		this.el = document.createElement('div');
		this.store = store;
	}

	render() {
		this.el.classList.add('reel');
		const muted = this.store.getVideoPlayerOptions().muted;
		const mobileSource = this.reel.sources.reduce((acc, curr) => {
			if (curr.target === 'mobile' && curr.width > acc.width) {
				return curr;
			}

			return acc;
		});
		const desktopSource = this.reel.sources.reduce((acc, curr) => {
			if (curr.target === 'desktop' && curr.width > acc.width) {
				return curr;
			}

			return acc;
		});

		this.el.innerHTML = `
	<video poster="${this.reel.posterUrl}" ${muted ? 'muted' : ''} loop playsinline>
		${this.reel.manifest ? `<source src="${this.reel.manifest.file}" type="${this.reel.manifest.type}">` : ''}
		<source src="${mobileSource.file}" type="${desktopSource.type}" media="(width <= 768px)">
		<source src="${desktopSource.file}" type="${desktopSource.type}" media="(width > 768px)">
	</video>
	<div class="reel-overlay">
		<div class="reel-meta">
			<span class="reel-tag">${this.reel.story}</span>
			<span class="reel-published-date">${new Date(this.reel.publishedAt).toLocaleDateString()}</span>
		</div>
		<div class="reel-info">
			<div class="reel-controls">
				<button class="mute-button">${muted ? unmuteIcon : muteIcon}</button>
				<div class="progress-bar">
					<div class="progress"></div>
					<div class="progress-handle"></div>
				</div>
				<div class="time-display">0:00 / 0:00</div>
			</div>
			<div class="reel-text">
				<div class="reel-title">${this.reel.title}</div>
				<div class="reel-description">
					${this.reel.description}
					${this.reel.article ? ` <div class="reel-article"> <a data-action="next-card" href="${this.reel.article.url}">${this.reel.article.label}</a> </div>` : ''}
				</div>
			</div>
		</div>
	</div>
	`;
	}

	activate(mobileMode: boolean) {
		if (mobileMode) {
			this.setupVisibilityObserver();
			this.#isActive = true;
			this.#hasStarted = false;
		} else {
			this.applyMuteState();
			this.play();
		}
	}

	deactivate(mobileMode: boolean) {
		if (mobileMode) {
			return;
		}
		this.pause();
	}

	private setupVisibilityObserver() {
		if (this.#visibilityObserver) {
			return;
		}

		this.#visibilityObserver = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!this.#isActive) {
						return;
					}

					// Only play when fully visible (100%)
					if (entry.intersectionRatio === 1.0) {
						this.#hasStarted = true;
						this.applyMuteState();
						this.play();
					} else if (this.#hasStarted && !entry.isIntersecting) {
						requestAnimationFrame(() => {
							this.pause();
						});
						this.teardownVisibilityObserver();
					}
				});
			},
			{
				threshold: [0, 1.0],
				root: this.el.closest('.article-view')
			}
		);

		this.#visibilityObserver.observe(this.el);
	}

	private teardownVisibilityObserver() {
		if (this.#visibilityObserver) {
			this.#visibilityObserver.disconnect();
			this.#visibilityObserver = null;
		}
	}

	private play() {
		const video = this.el.querySelector('video') as HTMLVideoElement;
		if (video) {
			video.play().catch((_err) => false);
		}
	}

	private pause() {
		const video = this.el.querySelector('video') as HTMLVideoElement;
		video.pause();
	}

	private applyMuteState() {
		const video = this.el.querySelector('video') as HTMLVideoElement;
		const muteButton = this.el.querySelector('.mute-button') as HTMLButtonElement;

		const muted = this.store.getVideoPlayerOptions().muted;
		video.muted = muted;
		muteButton.innerHTML = muted ? unmuteIcon : muteIcon;
	}

	registerEventListeners(): void {
		if (this.#isListening) {
			return;
		}
		this.#isListening = true;
		const card = this.el;
		const video = card.querySelector('video') as HTMLVideoElement;
		const text = card.querySelector('.reel-text') as HTMLElement;
		const muteButton = card.querySelector('.mute-button') as HTMLButtonElement;
		const progressBar = card.querySelector('.progress-bar') as HTMLElement;
		const progress = card.querySelector('.progress') as HTMLElement;
		const progressHandle = card.querySelector('.progress-handle') as HTMLElement;
		const timeDisplay = card.querySelector('.time-display') as HTMLElement;
		let isDragging = false;
		let activePointerId: number | null = null;

		// Tap to play/pause
		this.addEventListener(card, 'click', (event) => {
			if ((event.target as HTMLElement).closest('.reel-text')) {
				return;
			}
			if ((event.target as HTMLElement).closest('.mute-button')) {
				return;
			}
			if ((event.target as HTMLElement).closest('.progress-bar')) {
				return;
			}

			if (video.paused) {
				video.play();
			} else {
				video.pause();
			}
		});

		this.addEventListener(text, 'click', (event) => {
			if ((event.target as HTMLElement).localName === 'a') {
				return;
			}

			event.stopPropagation();
			text.classList.toggle('show-details');
		});

		this.addEventListener(muteButton, 'click', (event) => {
			event.stopPropagation();
			this.store.setVideoPlayerOptions({muted: !video.muted});
			this.applyMuteState();
		});

		const updateVideoTime = () => {
			const newTextContent = `${formatTime(video.currentTime)} / ${formatTime(video.duration)}`;
			if (timeDisplay.textContent !== newTextContent) {
				timeDisplay.textContent = newTextContent;
			}
		};

		this.addEventListener(video, 'timeupdate', () => {
			if (isDragging) {
				return;
			}

			updateVideoTime();
			const progressPercent = (video.currentTime / video.duration) * 100;
			updateScrubberPosition(progressPercent);
		});

		this.addEventListener(video, 'loadedmetadata', updateVideoTime);

		// Scrubber
		const stopDrag = (event: PointerEvent) => {
			if (activePointerId !== null && event.pointerId !== activePointerId) {
				return;
			}

			isDragging = false;
			activePointerId = null;
			card.classList.remove('dragging');
			progressHandle.releasePointerCapture(event.pointerId);
		};

		const updateScrubber = (event: PointerEvent) => {
			if (!isDragging || (activePointerId !== null && event.pointerId !== activePointerId)) {
				return;
			}

			event.preventDefault();
			const progressBarRect = progressBar.getBoundingClientRect();
			const clientX = event.clientX;
			let newTime = ((clientX - progressBarRect.left) / progressBarRect.width) * video.duration;
			newTime = Math.max(0, Math.min(newTime, video.duration)); // Clamp between 0 and duration
			video.currentTime = newTime;
			const progressPercent = (newTime / video.duration) * 100;
			updateScrubberPosition(progressPercent);
		};

		this.addEventListener(progressHandle, 'pointerdown', (event: PointerEvent) => {
			event.preventDefault();
			event.stopPropagation();
			isDragging = true;
			activePointerId = event.pointerId;
			card.classList.add('dragging');
			progressHandle.setPointerCapture(event.pointerId);
			updateScrubber(event);
		});

		this.addEventListener(progressHandle, 'pointermove', updateScrubber);
		this.addEventListener(progressHandle, 'pointerup', stopDrag);
		this.addEventListener(progressHandle, 'pointercancel', stopDrag);

		this.addEventListener(progressBar, 'click', (event: MouseEvent) => {
			event.stopPropagation();
			const progressBarRect = progressBar.getBoundingClientRect();
			const progressBarLeft = progressBarRect.left;
			const progressBarWidth = progressBarRect.width;
			const clickPosition = event.clientX - progressBarLeft;
			video.currentTime = (clickPosition / progressBarWidth) * video.duration;
		});

		const updateScrubberPosition = (progressPercent: number) => {
			const progressBarRect = progressBar.getBoundingClientRect();
			progress.style.scale = `${progressPercent}% 100%`;
			progressHandle.style.translate = `${(progressPercent / 100) * progressBarRect.width}px 0`;
		};
	}

	unregisterEventListeners(): void {
		for (const entry of [...this.#eventListeners]) {
			const [el, event, listener] = entry;
			el.removeEventListener(event, listener);
		}
		this.#eventListeners = [];
		this.#isListening = false;
	}

	private addEventListener<K extends keyof HTMLElementEventMap>(
		el: HTMLElement,
		type: K,
		listener: (this: HTMLElement, ev: HTMLElementEventMap[K]) => void
	) {
		// We have to cast to EventListener because the generic listener is more specific.
		// This is safe because the combination of K and HTMLElementEventMap[K] will always
		// be a valid combination for addEventListener.
		const typedListener = listener as EventListener;
		this.#eventListeners.push([el, type, typedListener]);
		el.addEventListener(type, typedListener);
	}
}

function formatTime(time: number): string {
	const minutes = Math.floor(time / 60).toString();
	const seconds = Math.floor(time % 60)
		.toString()
		.padStart(2, '0');
	return `${minutes}:${seconds}`;
}
